import { Ionicons } from '@expo/vector-icons';
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { Platform, Pressable, View } from 'react-native';
import Animated, { FadeInUp, FadeOutUp, useReducedMotion } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { palette } from '@/config/theme';

import { AppText } from './AppText';

export type ToastTone = 'success' | 'error' | 'info';

export interface ToastOptions {
  message: string;
  tone?: ToastTone;
  /** Milliseconds on screen before auto-dismiss. */
  duration?: number;
}

interface ActiveToast extends Required<Omit<ToastOptions, 'duration'>> {
  id: number;
}

interface ToastApi {
  show: (options: ToastOptions) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TONE: Record<ToastTone, { icon: keyof typeof Ionicons.glyphMap; color: string; border: string }> = {
  success: { icon: 'checkmark-circle', color: palette.voltBright, border: palette.voltLine },
  error: { icon: 'alert-circle', color: palette.loss, border: 'rgba(248,113,113,0.4)' },
  info: { icon: 'information-circle', color: palette.soft, border: palette.line },
};

/**
 * Lightweight glassy in-app notification toasts. A translucent Volt surface
 * (backdrop-blurred on web) slides in from the top, auto-dismisses, and stacks.
 * Mounted once at the root via ToastProvider; trigger with useToast().show().
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const insets = useSafeAreaInsets();
  const reducedMotion = useReducedMotion();
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    ({ message, tone = 'info', duration = 2600 }: ToastOptions) => {
      const id = nextId.current;
      nextId.current += 1;
      setToasts((current) => [...current.slice(-2), { id, message, tone }]);
      setTimeout(() => dismiss(id), duration);
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <View
        pointerEvents="box-none"
        style={{ position: 'absolute', top: insets.top + 8, left: 0, right: 0, alignItems: 'center' }}
      >
        {toasts.map((toast) => {
          const tone = TONE[toast.tone];
          return (
            <Animated.View
              key={toast.id}
              entering={reducedMotion ? undefined : FadeInUp.springify().damping(18)}
              exiting={reducedMotion ? undefined : FadeOutUp.duration(180)}
              style={{ width: '100%', maxWidth: 460, paddingHorizontal: 16, marginBottom: 8 }}
            >
              <Pressable
                accessibilityRole="alert"
                onPress={() => dismiss(toast.id)}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 10,
                  paddingHorizontal: 14,
                  paddingVertical: 12,
                  borderRadius: 16,
                  borderWidth: 1,
                  borderColor: tone.border,
                  backgroundColor: 'rgba(19,26,14,0.92)',
                  // True glass on web; native gets the translucent surface above.
                  ...(Platform.OS === 'web' ? { backdropFilter: 'blur(14px)' } : {}),
                  shadowColor: '#000',
                  shadowOpacity: 0.35,
                  shadowRadius: 16,
                  shadowOffset: { width: 0, height: 8 },
                  elevation: 8,
                }}
              >
                <Ionicons name={tone.icon} size={20} color={tone.color} />
                <AppText variant="caption" weight="medium" numberOfLines={2} className="flex-1 text-ink">
                  {toast.message}
                </AppText>
              </Pressable>
            </Animated.View>
          );
        })}
      </View>
    </ToastContext.Provider>
  );
}

/** Show glassy in-app toasts. Returns a no-op outside the provider. */
export function useToast(): ToastApi {
  return useContext(ToastContext) ?? { show: () => undefined };
}
